import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Users, 
  Store, 
  CreditCard, 
  TrendingUp, 
  Search, 
  Filter,
  MoreVertical,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronRight,
  BarChart3,
  ShieldCheck,
  MapPin,
  X,
  Download
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { BarberShop, Profile } from '../types';
import { Subscription } from '../types/subscription';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Logo } from './Logo';
import { useLanguage } from '../lib/LanguageContext';
import { ConfirmDialog } from './ConfirmDialog';

export const SuperAdminDashboard: React.FC = () => {
  const { t } = useLanguage();
  const [shops, setShops] = useState<BarberShop[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'shops' | 'users' | 'billing'>('shops');
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmConfig, setConfirmConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger'
  });
  const [editingShop, setEditingShop] = useState<BarberShop | null>(null);

  const filteredShops = shops.filter(s => 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSubscriptions = subscriptions.filter(s => {
    const shop = shops.find(sh => sh.id === s.shop_id || sh.id === (s as any).barber_shop_id);
    return shop?.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
           s.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
           s.plan_id?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const [stats, setStats] = useState({
    totalRevenue: 0,
    activeSubscriptions: 0,
    totalShops: 0,
    totalUsers: 0
  });

  useEffect(() => {
    fetchData();

    const handleClickOutside = () => setActiveMenu(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  const updateShopLocation = async (shopId: string, lat: number, lng: number) => {
    try {
      const { error } = await supabase
        .from('barber_shops')
        .update({ latitude: lat, longitude: lng })
        .eq('id', shopId);

      if (error) throw error;
      
      setShops(prev => prev.map(s => 
        s.id === shopId ? { ...s, latitude: lat, longitude: lng } : s
      ));
      setEditingShop(null);
      toast.success(t('admin.shopLocationUpdated'));
    } catch (error) {
      console.error('Error updating shop location:', error);
      toast.error(t('admin.failedUpdateLocation'));
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [shopsRes, subsRes, profilesRes] = await Promise.all([
        supabase.from('barber_shops').select('*').order('created_at', { ascending: false }),
        supabase.from('subscriptions').select('*'),
        supabase.from('profiles').select('*')
      ]);

      if (shopsRes.data) setShops(shopsRes.data);
      if (subsRes.data) setSubscriptions(subsRes.data);
      if (profilesRes.data) setProfiles(profilesRes.data);

      const activeSubs = subsRes.data?.filter(s => s.status === 'active') || [];
      const totalRevenue = activeSubs.reduce((acc, sub) => {
        const price = sub.plan_id === 'annual' ? 499.90 : 
                     sub.plan_id === 'basic_annual' ? 299.90 :
                     sub.plan_id === 'basic' ? 29.99 : 49.99;
        return acc + price;
      }, 0);

      setStats({
        totalRevenue,
        activeSubscriptions: activeSubs.length,
        totalShops: shopsRes.data?.length || 0,
        totalUsers: profilesRes.data?.length || 0
      });
    } catch (error) {
      console.error('Error fetching super admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportShops = () => {
    const headers = ["ID", "Name", "Address", "Created At", "Active"];
    const rows = filteredShops.map(s => [
      s.id,
      `"${s.name}"`,
      `"${s.address}"`,
      s.created_at,
      s.is_active ? 'Yes' : 'No'
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `shops_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const handleExportSubscriptions = () => {
    const headers = ["ID", "Shop ID", "Plan ID", "Status", "Current Period End"];
    const rows = filteredSubscriptions.map(s => [
      s.id,
      s.shop_id || (s as any).barber_shop_id,
      s.plan_id,
      s.status,
      s.current_period_end
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `subscriptions_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const toggleShopStatus = async (shopId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('barber_shops')
        .update({ is_active: !currentStatus })
        .eq('id', shopId);

      if (error) throw error;
      
      setShops(prev => prev.map(s => 
        s.id === shopId ? { ...s, is_active: !currentStatus } : s
      ));
    } catch (error) {
      console.error('Error toggling shop status:', error);
    }
  };

  const deleteShop = async (shopId: string) => {
    setConfirmConfig({
      isOpen: true,
      title: t('admin.deleteShop'),
      message: t('admin.deleteShopConfirm'),
      variant: 'danger',
      onConfirm: async () => {
        try {
          const { error } = await supabase
            .from('barber_shops')
            .delete()
            .eq('id', shopId);

          if (error) throw error;
          
          setShops(prev => prev.filter(s => s.id !== shopId));
        } catch (error) {
          console.error('Error deleting shop:', error);
        }
      }
    });
  };

  const updateUserRole = async (userId: string, newRole: Profile['role']) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) throw error;
      
      setProfiles(prev => prev.map(p => 
        p.id === userId ? { ...p, role: newRole } : p
      ));
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  const updateUserShop = async (userId: string, shopId: string | null) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ barber_shop_id: shopId })
        .eq('id', userId);

      if (error) throw error;
      
      setProfiles(prev => prev.map(p => 
        p.id === userId ? { ...p, barber_shop_id: shopId || undefined } : p
      ));
    } catch (error) {
      console.error('Error updating user shop:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-zinc-900 dark:border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 p-8">
      <div className="max-w-[1600px] mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white dark:bg-zinc-900 px-6 py-3 rounded-2xl border border-zinc-100 dark:border-zinc-800 shadow-sm text-left">
              <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 mb-1">{t('admin.totalRevenue')}</p>
              <p className="text-2xl font-black text-zinc-900 dark:text-white">CHF {stats.totalRevenue.toLocaleString()}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-6 text-right">
            <div>
              <div className="flex items-center justify-end gap-2 mb-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.systemOverview')}</span>
                <ShieldCheck className="text-amber-500" size={18} />
              </div>
              <h1 className="text-3xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.superAdminDashboard')}</h1>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { label: t('admin.activeSubscriptions'), value: stats.activeSubscriptions, icon: CreditCard, color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
            { label: t('admin.shops'), value: stats.totalShops, icon: Store, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20' },
            { label: t('admin.totalPlatformUsers'), value: stats.totalUsers, icon: Users, color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/20' },
            { label: t('admin.monthlyGrowth'), value: '+12%', icon: TrendingUp, color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20' }
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white dark:bg-zinc-900 p-6 rounded-[2.5rem] border border-zinc-100 dark:border-zinc-800 shadow-xl shadow-zinc-200/50 dark:shadow-black/50"
            >
              <div className={`w-12 h-12 ${stat.bg} ${stat.color} rounded-2xl flex items-center justify-center mb-4`}>
                <stat.icon size={24} aria-hidden="true" />
              </div>
              <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 mb-1">{stat.label}</p>
              <p className="text-3xl font-black text-zinc-900 dark:text-white">{stat.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <aside className="w-full lg:w-56 shrink-0">
            <div className="lg:sticky lg:top-8 space-y-2 bg-white dark:bg-zinc-900 p-3 rounded-[2.5rem] border border-zinc-100 dark:border-zinc-800 shadow-xl shadow-zinc-200/50 dark:shadow-black/50">
              <button 
                onClick={() => setActiveTab('shops')}
                className={`w-full flex items-center gap-3 px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeTab === 'shops' ? 'bg-zinc-900 dark:bg-amber-500 text-white dark:text-zinc-900 shadow-lg' : 'text-zinc-400 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                }`}
              >
                <Store size={18} /> {t('admin.shops')}
              </button>
              <button 
                onClick={() => setActiveTab('users')}
                className={`w-full flex items-center gap-3 px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeTab === 'users' ? 'bg-zinc-900 dark:bg-amber-500 text-white dark:text-zinc-900 shadow-lg' : 'text-zinc-400 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                }`}
              >
                <Users size={18} /> {t('admin.users') || 'Users'}
              </button>
              <button 
                onClick={() => setActiveTab('billing')}
                className={`w-full flex items-center gap-3 px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeTab === 'billing' ? 'bg-zinc-900 dark:bg-amber-500 text-white dark:text-zinc-900 shadow-lg' : 'text-zinc-400 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                }`}
              >
                <CreditCard size={18} /> {t('admin.subscription')}
              </button>
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0 grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Main List */}
            <div className="xl:col-span-2 space-y-6">
              <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden">
                <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">
                    {activeTab === 'shops' ? t('admin.shops') : activeTab === 'users' ? t('admin.platformUsers') : t('admin.billingTransactions')}
                  </h2>
                  <div className="flex gap-2 w-full md:w-auto">
                    <div className="relative flex-grow md:flex-grow-0">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300" size={16} />
                      <input 
                        type="text" 
                        placeholder={activeTab === 'shops' ? t('admin.searchShops') : activeTab === 'users' ? t('admin.searchUsers') : t('admin.searchTransactions')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-4 py-2 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-xl text-xs font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none w-full md:w-64 text-zinc-900 dark:text-white"
                      />
                    </div>
                    {(activeTab === 'shops' || activeTab === 'billing') && (
                      <button 
                        onClick={activeTab === 'shops' ? handleExportShops : handleExportSubscriptions}
                        className="px-4 py-2 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 text-zinc-900 dark:text-zinc-300 rounded-xl font-bold text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all flex items-center justify-center gap-2"
                      >
                        <Download size={14} /> CSV
                      </button>
                    )}
                  </div>
                </div>
                
                {activeTab === 'shops' ? (
                  <>
                    <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-zinc-50/50 dark:bg-zinc-800/50">
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.shopName')}</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('common.address')}</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.customer')}</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('common.status')}</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                      {shops
                        .filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()))
                        .map((shop) => {
                          const sub = subscriptions.find(s => s.shop_id === shop.id);
                          const owner = profiles.find(p => p.id === sub?.owner_id);
                          const isActive = shop.is_active !== false;
                          
                          return (
                            <tr key={shop.id} className={`hover:bg-zinc-50/30 dark:hover:bg-zinc-800/30 transition-colors ${!isActive ? 'opacity-50 grayscale' : ''}`}>
                              <td className="p-6">
                                <div className="font-black text-zinc-900 dark:text-white">{shop.name}</div>
                                <div className="text-[10px] font-bold text-zinc-400 dark:text-zinc-300">{shop.address}</div>
                              </td>
                              <td className="p-6">
                                <div className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400">
                                  {shop.latitude && shop.longitude ? (
                                    <span>{shop.latitude.toFixed(4)}, {shop.longitude.toFixed(4)}</span>
                                  ) : (
                                    <span className="italic">{t('common.na')}</span>
                                  )}
                                </div>
                              </td>
                              <td className="p-6">
                                <div className="text-xs font-bold text-zinc-900 dark:text-white">{owner?.email || t('common.na')}</div>
                              </td>
                              <td className="p-6">
                                <div className="flex flex-col gap-2">
                                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest w-fit ${
                                    sub?.status === 'active' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                                  }`}>
                                    {sub?.status === 'active' ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                                    {sub?.status || t('common.none')}
                                  </div>
                                  {!isActive && (
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-300 w-fit">
                                      <Clock size={12} /> {t('common.cancelled')}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="p-6">
                                <div className="relative">
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveMenu(activeMenu === shop.id ? null : shop.id);
                                    }}
                                    aria-label="Shop Actions"
                                    className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-colors"
                                  >
                                    <MoreVertical size={18} className="text-zinc-400 dark:text-zinc-300" aria-hidden="true" />
                                  </button>

                                  {activeMenu === shop.id && (
                                    <motion.div 
                                      initial={{ opacity: 0, scale: 0.95, y: 10 }}
                                      animate={{ opacity: 1, scale: 1, y: 0 }}
                                      className="absolute right-0 mt-2 w-48 bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl border border-zinc-100 dark:border-zinc-800 z-50 py-2"
                                    >
                                      <button 
                                        onClick={() => toggleShopStatus(shop.id, isActive)}
                                        className="w-full px-4 py-2 text-left text-[10px] font-black uppercase tracking-widest text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 flex items-center gap-2"
                                      >
                                        {isActive ? <XCircle size={14} /> : <CheckCircle2 size={14} />}
                                        {isActive ? t('admin.clearAvailability').replace('Availability', 'Shop') : t('nav.booking').replace('Booking', 'Enable Shop')}
                                      </button>
                                      <button 
                                        onClick={() => setEditingShop(shop)}
                                        className="w-full px-4 py-2 text-left text-[10px] font-black uppercase tracking-widest text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 flex items-center gap-2"
                                      >
                                        <MapPin size={14} /> {t('admin.editLocation')}
                                      </button>
                                      <button 
                                        onClick={() => deleteShop(shop.id)}
                                        className="w-full px-4 py-2 text-left text-[10px] font-black uppercase tracking-widest text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                      >
                                        <XCircle size={14} /> {t('admin.deleteShop')}
                                      </button>
                                    </motion.div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Shop Cards */}
                <div className="sm:hidden grid grid-cols-1 gap-4 p-4">
                  {shops
                    .filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map((shop) => {
                      const sub = subscriptions.find(s => s.shop_id === shop.id);
                      const owner = profiles.find(p => p.id === sub?.owner_id);
                      const isActive = shop.is_active !== false;
                      
                      return (
                        <div key={shop.id} className={`bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-800 space-y-4 ${!isActive ? 'opacity-50 grayscale' : ''}`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-black text-zinc-900 dark:text-white">{shop.name}</div>
                              <div className="text-[10px] font-bold text-zinc-400 dark:text-zinc-300">{shop.address}</div>
                            </div>
                            <button 
                              onClick={() => setActiveMenu(activeMenu === shop.id ? null : shop.id)}
                              className="p-2 bg-white dark:bg-zinc-900 rounded-lg shadow-sm"
                            >
                              <MoreVertical size={16} className="text-zinc-400" />
                            </button>
                          </div>

                          <div className="flex flex-wrap gap-2">
                             <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
                                sub?.status === 'active' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                              }`}>
                                {sub?.status === 'active' ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
                                {sub?.status || 'No Sub'}
                              </div>
                              {!isActive && (
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-300">
                                  <Clock size={10} /> {t('common.cancelled')}
                                </div>
                              )}
                          </div>

                          <div className="text-xs font-bold text-zinc-500 dark:text-zinc-300">
                             {t('admin.customer')}: {owner?.email || t('common.na')}
                          </div>

                          {activeMenu === shop.id && (
                            <div className="grid grid-cols-2 gap-2 pt-2">
                              <button 
                                onClick={() => toggleShopStatus(shop.id, isActive)}
                                className="px-3 py-2 bg-zinc-100 dark:bg-zinc-700 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
                              >
                                {isActive ? <XCircle size={12} /> : <CheckCircle2 size={12} />}
                                {isActive ? 'Disable' : 'Enable'}
                              </button>
                              <button 
                                onClick={() => setEditingShop(shop)}
                                className="px-3 py-2 bg-zinc-100 dark:bg-zinc-700 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
                              >
                                <MapPin size={12} /> Location
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </>
            ) : activeTab === 'users' ? (
                  <>
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-zinc-50/50 dark:bg-zinc-800/50">
                            <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.user')}</th>
                            <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.roles')}</th>
                            <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">Assigned {t('admin.shop')}</th>
                            <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                          {profiles
                            .filter(p => p.role !== 'customer')
                            .filter(p => p.email.toLowerCase().includes(searchQuery.toLowerCase()))
                            .map((profile) => {
                              const assignedShop = shops.find(s => s.id === profile.barber_shop_id);
                              
                              return (
                                <tr key={profile.id} className="hover:bg-zinc-50/30 dark:hover:bg-zinc-800/30 transition-colors">
                                  <td className="p-6">
                                    <div className="font-black text-zinc-900 dark:text-white">{profile.email}</div>
                                    <div className="text-[10px] font-bold text-zinc-400 dark:text-zinc-300">ID: {profile.id.slice(0, 8)}...</div>
                                  </td>
                                  <td className="p-6">
                                    <select 
                                      value={profile.role}
                                      onChange={(e) => updateUserRole(profile.id, e.target.value as Profile['role'])}
                                      className="bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-lg text-[10px] font-black uppercase tracking-widest text-zinc-600 dark:text-zinc-300 focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none p-2"
                                    >
                                      <option value="customer" className="dark:bg-zinc-900">{t('admin.customerRole')}</option>
                                      <option value="barber_staff" className="dark:bg-zinc-900">{t('admin.barberStaff')}</option>
                                      <option value="super_admin" className="dark:bg-zinc-900">{t('admin.superAdmin')}</option>
                                    </select>
                                  </td>
                                  <td className="p-6">
                                    <select 
                                      value={profile.barber_shop_id || ''}
                                      onChange={(e) => updateUserShop(profile.id, e.target.value || null)}
                                      className="bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-lg text-[10px] font-black uppercase tracking-widest text-zinc-600 dark:text-zinc-300 focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none p-2 w-full max-w-[200px]"
                                    >
                                      <option value="" className="dark:bg-zinc-900">{t('admin.noShopAssigned')}</option>
                                      {shops.map(shop => (
                                        <option key={shop.id} value={shop.id} className="dark:bg-zinc-900">{shop.name}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="p-6">
                                    <div className="text-[10px] font-bold text-zinc-400 dark:text-zinc-300">
                                      {t('admin.joined')} {format(new Date(profile.created_at), 'MMM yyyy')}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Users Cards */}
                    <div className="lg:hidden grid grid-cols-1 gap-4 p-4">
                       {profiles
                        .filter(p => p.role !== 'customer')
                        .filter(p => p.email.toLowerCase().includes(searchQuery.toLowerCase()))
                        .map((profile) => (
                          <div key={profile.id} className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-800 space-y-4">
                            <div>
                               <div className="font-black text-zinc-900 dark:text-white truncate">{profile.email}</div>
                               <div className="text-[10px] font-bold text-zinc-400">{t('admin.joined')} {format(new Date(profile.created_at), 'MMM yyyy')}</div>
                            </div>
                            <div className="grid grid-cols-1 gap-3 pt-2">
                               <div className="flex flex-col gap-1">
                                  <span className="text-[8px] font-black uppercase text-zinc-400">{t('admin.roles')}</span>
                                  <select 
                                    value={profile.role}
                                    onChange={(e) => updateUserRole(profile.id, e.target.value as Profile['role'])}
                                    className="w-full bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-700 rounded-xl text-[10px] font-black uppercase p-3"
                                  >
                                    <option value="customer">{t('admin.customerRole')}</option>
                                    <option value="barber_staff">{t('admin.barberStaff')}</option>
                                    <option value="super_admin">{t('admin.superAdmin')}</option>
                                  </select>
                               </div>
                               <div className="flex flex-col gap-1">
                                  <span className="text-[8px] font-black uppercase text-zinc-400">{t('admin.shop')}</span>
                                  <select 
                                    value={profile.barber_shop_id || ''}
                                    onChange={(e) => updateUserShop(profile.id, e.target.value || null)}
                                    className="w-full bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-700 rounded-xl text-[10px] font-black uppercase p-3"
                                  >
                                    <option value="">{t('admin.noShopAssigned')}</option>
                                    {shops.map(shop => (
                                      <option key={shop.id} value={shop.id}>{shop.name}</option>
                                    ))}
                                  </select>
                               </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-zinc-50/50 dark:bg-zinc-800/50">
                            <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('common.date')}</th>
                            <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.shop')}</th>
                            <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">Plan</th>
                            <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('common.amount')}</th>
                            <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('common.status')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                          {subscriptions
                            .filter(sub => {
                              const shop = shops.find(s => s.id === sub.shop_id);
                              return shop?.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                                     sub.status.toLowerCase().includes(searchQuery.toLowerCase());
                            })
                            .map((sub) => {
                              const shop = shops.find(s => s.id === sub.shop_id);
                              const price = sub.plan_id === 'annual' ? '499.90' : 
                                           sub.plan_id === 'basic_annual' ? '299.90' :
                                           sub.plan_id === 'basic' ? '29.99' : '49.99';
                              
                              return (
                                <tr key={sub.id} className="hover:bg-zinc-50/30 dark:hover:bg-zinc-800/30 transition-colors">
                                  <td className="p-6">
                                    <div className="text-xs font-bold text-zinc-900 dark:text-white">
                                      {format(new Date(sub.created_at), 'MMM d, yyyy')}
                                    </div>
                                  </td>
                                  <td className="p-6">
                                    <div className="font-black text-zinc-900 dark:text-white">{shop?.name || t('admin.noShop')}</div>
                                  </td>
                                  <td className="p-6">
                                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">
                                      {sub.plan_id.replace('_', ' ')}
                                    </div>
                                  </td>
                                  <td className="p-6">
                                    <div className="text-xs font-black text-zinc-900 dark:text-white">CHF {price}</div>
                                  </td>
                                  <td className="p-6">
                                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                      sub.status === 'active' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                                    }`}>
                                      {sub.status}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Subscriptions Cards */}
                    <div className="lg:hidden grid grid-cols-1 gap-4 p-4">
                       {subscriptions
                        .filter(sub => {
                          const shop = shops.find(s => s.id === sub.shop_id);
                          return shop?.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                                 sub.status.toLowerCase().includes(searchQuery.toLowerCase());
                        })
                        .map((sub) => {
                          const shop = shops.find(s => s.id === sub.shop_id);
                          const price = sub.plan_id === 'annual' ? '499.90' : 
                                           sub.plan_id === 'basic_annual' ? '299.90' :
                                           sub.plan_id === 'basic' ? '29.99' : '49.99';
                                           
                          return (
                             <div key={sub.id} className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-800 space-y-4">
                                <div className="flex justify-between items-start">
                                   <div>
                                      <div className="font-black text-zinc-900 dark:text-white uppercase tracking-tighter">{shop?.name}</div>
                                      <div className="text-[10px] font-bold text-zinc-500">{format(new Date(sub.created_at), 'MMM d, yyyy')}</div>
                                   </div>
                                   <div className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase ${
                                     sub.status === 'active' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                                   }`}>
                                      {sub.status}
                                   </div>
                                </div>
                                <div className="flex justify-between items-center pt-2 border-t border-zinc-100 dark:border-zinc-800">
                                   <span className="text-[9px] font-black uppercase text-zinc-400">{sub.plan_id.replace('_', ' ')}</span>
                                   <div className="font-black text-amber-500 text-sm">CHF {price}</div>
                                </div>
                             </div>
                          );
                        })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Recent Subscriptions */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-zinc-900 p-8 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50">
              <h2 className="text-xl font-black uppercase tracking-tighter mb-6 flex items-center gap-3 text-zinc-900 dark:text-white">
                <CreditCard size={24} /> {t('admin.recentBilling')}
              </h2>
              <div className="space-y-4">
                {subscriptions.slice(0, 5).map((sub) => {
                  const price = sub.plan_id === 'annual' ? '499.90' : 
                               sub.plan_id === 'basic_annual' ? '299.90' :
                               sub.plan_id === 'basic' ? '29.99' : '49.99';
                  return (
                    <div key={sub.id} className="p-4 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl border border-zinc-100 dark:border-zinc-800 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white dark:bg-zinc-900 rounded-xl flex items-center justify-center shadow-sm border border-zinc-100 dark:border-zinc-800">
                          <BarChart3 className="text-zinc-400 dark:text-zinc-300" size={18} />
                        </div>
                        <div>
                          <p className="text-xs font-black text-zinc-900 dark:text-white">CHF {price}</p>
                          <p className="text-[10px] font-bold text-zinc-400 dark:text-zinc-300">{format(new Date(sub.created_at), 'MMM d, yyyy')}</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-zinc-300 dark:text-zinc-300" />
                    </div>
                  );
                })}
              </div>
              <button 
                onClick={() => setActiveTab('billing')}
                className="w-full mt-6 py-4 border-2 border-zinc-100 dark:border-zinc-800 rounded-2xl text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-all"
              >
                {t('admin.viewAllTransactions')}
              </button>
            </div>

            <div className="bg-zinc-900 dark:bg-amber-500 p-8 rounded-[3rem] shadow-2xl text-white dark:text-zinc-900 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <TrendingUp size={80} />
              </div>
              <h3 className="text-lg font-black uppercase tracking-tighter mb-2">{t('admin.platformHealth')}</h3>
              <p className="text-zinc-400 dark:text-zinc-900/70 text-xs font-bold leading-relaxed mb-6">
                {t('admin.platformHealthDesc')}
              </p>
              <div className="flex items-center gap-2 text-emerald-400 dark:text-zinc-900">
                <CheckCircle2 size={16} />
                <span className="text-[10px] font-black uppercase tracking-widest">{t('admin.systemStable')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Edit Location Modal */}
        {editingShop && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-zinc-900 w-full max-w-md rounded-[3rem] p-10 shadow-2xl border border-zinc-100 dark:border-zinc-800"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('common.edit')} <span className="text-zinc-400 dark:text-zinc-300">{t('admin.addressLocation').split(' ')[2]}</span></h3>
                <button onClick={() => setEditingShop(null)} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-colors">
                  <X size={20} className="text-zinc-400 dark:text-zinc-300" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="p-4 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl border border-zinc-100 dark:border-zinc-800">
                  <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 mb-1">{t('admin.shop')}</p>
                  <p className="text-sm font-black text-zinc-900 dark:text-white">{editingShop.name}</p>
                  <p className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400">{editingShop.address}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.latitude')}</label>
                    <input 
                      type="number" 
                      step="any"
                      className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400"
                      defaultValue={editingShop.latitude || ''}
                      id="shop-lat"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.longitude')}</label>
                    <input 
                      type="number" 
                      step="any"
                      className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400"
                      defaultValue={editingShop.longitude || ''}
                      id="shop-lng"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button 
                    onClick={() => setEditingShop(null)}
                    className="flex-1 py-4 bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 rounded-2xl font-black uppercase tracking-widest hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-all"
                  >
                    {t('common.cancel')}
                  </button>
                  <button 
                    onClick={() => {
                      const lat = parseFloat((document.getElementById('shop-lat') as HTMLInputElement).value);
                      const lng = parseFloat((document.getElementById('shop-lng') as HTMLInputElement).value);
                      if (!isNaN(lat) && !isNaN(lng)) {
                        updateShopLocation(editingShop.id, lat, lng);
                      } else {
                        toast.error(t('admin.enterValidCoords'));
                      }
                    }}
                    className="flex-1 py-4 bg-zinc-900 dark:bg-amber-500 text-white dark:text-zinc-900 rounded-2xl font-black uppercase tracking-widest hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/50"
                  >
                    {t('common.save')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmConfig.isOpen}
          onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
          onConfirm={confirmConfig.onConfirm}
          title={confirmConfig.title}
          message={confirmConfig.message}
          variant={confirmConfig.variant}
        />
      </div>
    );
  };
